import { useState, useEffect, useCallback } from 'react';

const ROICalculator = () => {
  const [teamSize, setTeamSize] = useState(5);
  const [monthlyActions, setMonthlyActions] = useState(3);
  const [avgProcessingWeeks, setAvgProcessingWeeks] = useState(16);

  const [results, setResults] = useState({
    timeSaved: 0,
    efficiencyGain: 0,
    additionalCapacity: 0,
    overallROI: 0
  });

  const calculateROI = useCallback(() => {
    const complexityFactor = Math.min(1, avgProcessingWeeks / 24);
    const timeReductionRate = 0.70 + (complexityFactor * 0.12);
    const weeksPerActionSaved = avgProcessingWeeks * timeReductionRate;
    const totalWeeksSavedMonthly = Math.round(weeksPerActionSaved * monthlyActions);
    
    const baseMultiplier = 2.5;
    const teamScaleFactor = Math.max(0.8, 1 - (teamSize * 0.02));
    const effectiveMultiplier = baseMultiplier * teamScaleFactor;
    
    const currentAnnualCapacity = teamSize * 12 * (monthlyActions / teamSize);
    const additionalActionsPerYear = Math.round(currentAnnualCapacity * (effectiveMultiplier - 1));
    
    const rawEfficiencyGain = (effectiveMultiplier - 1) * 100;
    const efficiencyGain = Math.min(Math.round(rawEfficiencyGain), 175);
    
    const timeSavingsComponent = timeReductionRate * 40;
    const capacityComponent = Math.min((effectiveMultiplier - 1) / 2, 0.75) * 30;
    const automationComponent = 0.77 * 30;
    
    const rawROI = timeSavingsComponent + capacityComponent + automationComponent;
    
    const minROI = 52 + (avgProcessingWeeks / 32) * 8;
    const maxROI = 75;
    const overallROI = Math.round(Math.min(Math.max(rawROI, minROI), maxROI));

    setResults({
      timeSaved: totalWeeksSavedMonthly,
      efficiencyGain: efficiencyGain,
      additionalCapacity: additionalActionsPerYear,
      overallROI: overallROI
    });
  }, [teamSize, monthlyActions, avgProcessingWeeks]);

  useEffect(() => {
    calculateROI();
  }, [calculateROI]);

  const ResultCard = ({ label, value, suffix, highlight, description }) => (
    <div 
      className={`rounded-2xl p-5 text-center transition-all duration-300 ${
        highlight 
          ? 'bg-gradient-to-br from-purple-600 to-indigo-700 text-white shadow-lg shadow-purple-200' 
          : 'bg-white border border-gray-200 hover:border-purple-300 hover:shadow-md'
      }`}
    >
      <div className={`text-3xl font-bold mb-1 ${highlight ? 'text-white' : 'text-gray-900'}`}>
        {value}{suffix}
      </div>
      <div className={`text-sm font-medium ${highlight ? 'text-purple-100' : 'text-gray-600'}`}>
        {label}
      </div>
      {description && (
        <div className={`text-xs mt-2 ${highlight ? 'text-purple-200' : 'text-gray-400'}`}>
          {description}
        </div>
      )}
    </div>
  );

  return (
    <section className="py-16 px-6 bg-gradient-to-b from-gray-50 to-white" data-testid="roi-calculator-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
            Interactive Calculator
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Calculate Your ROI with AcuSightPro
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Adjust the sliders to see realistic savings based on your team's current workload. 
            Results are calculated from actual federal acquisition benchmarks.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Sliders Panel */}
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-bold">1</span>
              Enter Your Current State
            </h3>
            
            <div className="space-y-8 flex-1">
              {/* Team Size Slider */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-700">Team Size</label>
                  <span className="text-lg font-bold text-purple-600">{teamSize} COs</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="25"
                  value={teamSize}
                  onChange={(e) => setTeamSize(parseInt(e.target.value))}
                  className="roi-slider"
                  style={{
                    background: `linear-gradient(to right, #7c3aed 0%, #7c3aed ${((teamSize - 1) / 24) * 100}%, #e5e7eb ${((teamSize - 1) / 24) * 100}%, #e5e7eb 100%)`
                  }}
                  data-testid="slider-team-size"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>1 COs</span>
                  <span className="text-gray-400">Contracting officers</span>
                  <span>25 COs</span>
                </div>
              </div>

              {/* Actions Per Month Slider */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-700">Actions Per Month</label>
                  <span className="text-lg font-bold text-purple-600">{monthlyActions} actions</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="15"
                  value={monthlyActions}
                  onChange={(e) => setMonthlyActions(parseInt(e.target.value))}
                  className="roi-slider"
                  style={{
                    background: `linear-gradient(to right, #7c3aed 0%, #7c3aed ${((monthlyActions - 1) / 14) * 100}%, #e5e7eb ${((monthlyActions - 1) / 14) * 100}%, #e5e7eb 100%)`
                  }}
                  data-testid="slider-actions-per-month"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>1 actions</span>
                  <span className="text-gray-400">Acquisitions started</span>
                  <span>15 actions</span>
                </div>
              </div>

              {/* Processing Time Slider */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-700">Current Processing Time</label>
                  <span className="text-lg font-bold text-purple-600">{avgProcessingWeeks} weeks</span>
                </div>
                <input
                  type="range"
                  min="4"
                  max="32"
                  value={avgProcessingWeeks}
                  onChange={(e) => setAvgProcessingWeeks(parseInt(e.target.value))}
                  className="roi-slider"
                  style={{
                    background: `linear-gradient(to right, #7c3aed 0%, #7c3aed ${((avgProcessingWeeks - 4) / 28) * 100}%, #e5e7eb ${((avgProcessingWeeks - 4) / 28) * 100}%, #e5e7eb 100%)`
                  }}
                  data-testid="slider-processing-time"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>4 weeks</span>
                  <span className="text-gray-400">Avg. time to decision</span>
                  <span>32 weeks</span>
                </div>
              </div>
            </div>

            <div className="mt-auto pt-8">
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Your Current State</div>
                <div className="text-sm text-gray-700">
                  <span className="font-semibold">{teamSize}</span> COs processing <span className="font-semibold">{monthlyActions}</span> actions/month, 
                  each taking <span className="font-semibold">{avgProcessingWeeks}</span> weeks on average.
                </div>
              </div>
            </div>
          </div>

          {/* Results Panel */}
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-bold">2</span>
              Your Projected Savings
            </h3>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <ResultCard
                label="Overall ROI"
                value={results.overallROI}
                suffix="%"
                highlight={true}
                description="Efficiency improvement"
              />
              <ResultCard
                label="Time Saved"
                value={results.timeSaved}
                suffix=" wks"
                description="Per month"
              />
              <ResultCard
                label="Capacity Gain"
                value={results.efficiencyGain}
                suffix="%"
                description="More throughput"
              />
              <ResultCard
                label="Extra Actions"
                value={results.additionalCapacity}
                suffix="/yr"
                description="Additional capacity"
              />
            </div>

            <div className="p-5 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100">
              <div className="text-sm font-semibold text-purple-900 mb-3">What This Means</div>
              <ul className="space-y-2 text-sm text-purple-800">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5">→</span>
                  <span>
                    Your team could handle <strong>{results.additionalCapacity}</strong> more acquisitions annually
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5">→</span>
                  <span>
                    Processing time drops from <strong>{avgProcessingWeeks} weeks</strong> to <strong>{Math.max(2, Math.round(avgProcessingWeeks * 0.2))} weeks</strong>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5">→</span>
                  <span>
                    <strong>70-85%</strong> of manual administrative work automated
                  </span>
                </li>
              </ul>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">
              Estimates based on federal acquisition benchmarks. Actual results vary by complexity and process maturity.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
